import { Component } from '@theme/component';

/** Imported reviews retain their source text; only filtering and order change. */
class ReviewArchive extends Component {
  #reviews;
  #loading;
  #controller;
  #observer;
  #page = 1;
  #labels;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#labels = JSON.parse(this.querySelector('[data-labels]').textContent);
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.#observer.disconnect();
        this.#refresh(false);
      }
    }, { rootMargin: '500px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#loading = undefined;
  }

  #click = (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.hasAttribute('data-rating')) {
      this.querySelector('[data-rating-select]').value = button.dataset.rating;
      this.#page = 1;
    } else if (button.hasAttribute('data-prev')) {
      this.#page--;
    } else if (button.hasAttribute('data-next')) {
      this.#page++;
    } else return;
    this.#refresh(true);
  };

  #change = (event) => {
    if (event.target.matches('[data-page]')) this.#page = Number(event.target.value);
    else if (event.target.matches('[data-rating-select], [data-sort]')) this.#page = 1;
    else return;
    this.#refresh(true);
  };

  async #load() {
    if (this.#reviews) return;
    this.#loading ||= fetch(this.dataset.source, { signal: this.#controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Review collection unavailable');
        return response.json();
      }).then((reviews) => { this.#reviews = reviews; })
      .finally(() => { this.#loading = undefined; });
    await this.#loading;
  }

  #text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), this.#labels[key]);
  }

  #element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  #card(review) {
    const card = this.#element('article', 'hf-archive-card');
    const person = this.#element('div', 'hf-archive-card__person');
    person.append(this.#element('strong', '', review.reviewer_name));
    if (review.source_verified) person.append(this.#element('span', 'hf-archive-card__verified', `✓ ${this.#text('verified')}`));
    if (review.recommended) person.append(this.#element('span', '', this.#text('recommended')));
    const content = this.#element('div', 'hf-archive-card__content');
    const meta = this.#element('div', 'hf-archive-card__meta');
    const stars = this.#element('span', 'hf-archive-card__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
    stars.setAttribute('aria-label', this.querySelector(`[data-rating-select] option[value="${review.rating}"]`).textContent);
    const time = this.#element('time', '', new Date(`${review.review_date}T12:00:00Z`).toLocaleDateString(document.documentElement.lang || 'en', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }));
    time.dateTime = review.review_date;
    meta.append(stars, time);
    content.append(meta, this.#element('h3', '', review.title), this.#element('p', 'hf-archive-card__body', review.body), this.#element('p', 'hf-archive-card__helpful', `${this.#text('helpful')} 👍 ${review.helpful} 👎 ${review.unhelpful}`));
    card.append(person, content);
    return card;
  }

  async #refresh(moveFocus) {
    const status = this.querySelector('[data-review-status]');
    this.setAttribute('aria-busy', 'true');
    try {
      await this.#load();
      if (!this.isConnected) return;
      const rating = this.querySelector('[data-rating-select]').value;
      const sort = this.querySelector('[data-sort]').value;
      const reviews = this.#reviews.filter((review) => rating === 'all' || review.rating === Number(rating));
      reviews.sort((a, b) => {
        const newest = b.review_date.localeCompare(a.review_date);
        if (sort === 'highest') return b.rating - a.rating || newest;
        if (sort === 'lowest') return a.rating - b.rating || newest;
        if (sort === 'helpful') return b.helpful - a.helpful || newest;
        return newest;
      });
      const pages = Math.max(1, Math.ceil(reviews.length / 10));
      this.#page = Math.max(1, Math.min(pages, this.#page));
      const start = (this.#page - 1) * 10;
      const list = this.querySelector('[data-review-list]');
      list.replaceChildren(...reviews.slice(start, start + 10).map((review) => this.#card(review)));
      status.textContent = reviews.length ? this.#text('showing', { start: start + 1, end: Math.min(start + 10, reviews.length), count: reviews.length }) : this.#text('no_matches');
      const select = this.querySelector('[data-page]');
      select.replaceChildren(...Array.from({ length: pages }, (_, i) => new Option(String(i + 1), String(i + 1))));
      select.value = String(this.#page);
      this.querySelector('[data-page-count]').textContent = this.#text('page_of', { page: this.#page, pages });
      this.querySelector('[data-prev]').disabled = this.#page === 1;
      this.querySelector('[data-next]').disabled = this.#page === pages;
      this.querySelector('.hf-archive-pagination').hidden = pages <= 1;
      if (moveFocus) {
        list.focus({ preventScroll: true });
        list.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = this.#text('load_error');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('hf-review-archive')) customElements.define('hf-review-archive', ReviewArchive);
